/**
 * MessagesPage - Conversation list and chat view
 * Phase 11 Part 2: Messaging System
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import ChatWindow from '../components/ChatWindow';
import { 
  ArrowLeft, 
  MessageCircle, 
  Package, 
  Headphones,
  Store,
  User,
  Truck,
  Shield,
  Search,
  Filter,
  Plus,
  ChevronRight,
  Loader2
} from 'lucide-react';

// Conversation type icons
const TYPE_ICONS = {
  order_chat: Package,
  support: Headphones,
  merchant_chat: Store,
  dispatch_chat: Shield
};

// Role icons for avatars
const ROLE_ICONS = {
  customer: User,
  driver: Truck,
  merchant: Store,
  admin: Shield
};

export default function MessagesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedConversation, setSelectedConversation] = useState(
    searchParams.get('conversation') || null
  );

  const fetchConversations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('type', filter);
      
      const res = await api.get(`/messaging/conversations?${params}`);
      setConversations(res.data.conversations);
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Update URL when conversation selected
  useEffect(() => {
    if (selectedConversation) {
      setSearchParams({ conversation: selectedConversation });
    } else {
      setSearchParams({});
    }
  }, [selectedConversation, setSearchParams]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getConversationTitle = (conv) => {
    if (conv.title) return conv.title;
    if (conv.other_participant_name) return conv.other_participant_name;
    if (conv.order_id) return `Order #${conv.order_id.slice(0, 8)}`;
    return 'Conversation';
  };

  // Mobile view: Show either list or chat
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00A86B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => selectedConversation ? setSelectedConversation(null) : navigate(-1)}
                className="text-gray-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#00A86B]" />
                  Messages
                </h1>
              </div>
            </div>
            
            <Button 
              onClick={() => navigate('/support')}
              className="bg-[#00A86B] hover:bg-[#00A86B]/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Support
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex gap-6 h-[calc(100vh-180px)]">
          {/* Conversation List */}
          <div className={`w-full md:w-96 ${selectedConversation && isMobile ? 'hidden' : ''}`}>
            {/* Filters */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              {[
                { id: 'all', label: 'All' },
                { id: 'order_chat', label: 'Orders' },
                { id: 'support', label: 'Support' },
                { id: 'merchant_chat', label: 'Merchants' }
              ].map(f => (
                <Button
                  key={f.id}
                  variant={filter === f.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(f.id)}
                  className={filter === f.id 
                    ? 'bg-[#00A86B] hover:bg-[#00A86B]/90' 
                    : 'border-gray-200'
                  }
                >
                  {f.label}
                </Button>
              ))}
            </div>

            {/* Conversations */}
            <div className="space-y-2 overflow-y-auto max-h-[calc(100vh-280px)]">
              {conversations.length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No conversations yet</p>
                  <Button 
                    onClick={() => navigate('/support')}
                    className="mt-4 bg-[#00A86B]"
                  >
                    Start a Support Chat
                  </Button>
                </div>
              ) : (
                conversations.map(conv => {
                  const TypeIcon = TYPE_ICONS[conv.type] || MessageCircle;
                  const isSelected = selectedConversation === conv.id;
                  
                  return (
                    <Card
                      key={conv.id}
                      onClick={() => setSelectedConversation(conv.id)}
                      className={`p-4 cursor-pointer transition-all hover:border-[#00A86B]/50 ${
                        isSelected ? 'border-[#00A86B] bg-[#00A86B]/5' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          conv.type === 'support' ? 'bg-[#FFD300]/20' : 'bg-[#00A86B]/10'
                        }`}>
                          <TypeIcon className={`w-6 h-6 ${
                            conv.type === 'support' ? 'text-[#FFD300]' : 'text-[#00A86B]'
                          }`} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-gray-800 truncate">
                              {getConversationTitle(conv)}
                            </h3>
                            <span className="text-xs text-gray-400">
                              {formatTime(conv.last_message_at)}
                            </span>
                          </div>
                          
                          <p className="text-sm text-gray-500 truncate mt-1">
                            {conv.last_message_preview || 'No messages yet'}
                          </p>

                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={`text-xs ${
                              conv.type === 'support' 
                                ? 'bg-[#FFD300]/10 text-[#FFD300] border-[#FFD300]/30' 
                                : 'bg-[#00A86B]/10 text-[#00A86B] border-[#00A86B]/30'
                            }`}>
                              {conv.type.replace('_', ' ')}
                            </Badge>
                            
                            {conv.unread_count > 0 && (
                              <Badge className="bg-red-500 text-white text-xs">
                                {conv.unread_count}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <ChevronRight className="w-5 h-5 text-gray-300" />
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          </div>

          {/* Chat Window */}
          <div className={`flex-1 ${!selectedConversation && isMobile ? 'hidden' : ''}`}>
            {selectedConversation ? (
              <ChatWindow
                conversationId={selectedConversation}
                title={conversations.find(c => c.id === selectedConversation)?.title}
                onBack={isMobile ? () => setSelectedConversation(null) : null}
                className="h-full"
              />
            ) : (
              <div className="h-full bg-white rounded-xl border border-gray-200 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Select a conversation to start chatting</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
